import {PlayerSeatProps} from "../../../types/props";
import {
    CardPosition,
    GameState,
    PlayerCardHolder,
    TurnState,
    USER_ACTION_LABELS,
    UserAction
} from "../../../types/game";
import styles from "../GameScreen.module.scss";
import {ConnectionStatus} from "../../../types/lobby";
import {splitCardsToRows, toInitials} from "../../../utils";
import CardSlot from "./CardSlot";
import {useIsTouchDevice} from "../../../useIsTouchDevice";

const PlayerSeat = ({
                        game,
                        playerId,
                        isYou,
                        position,
                        playerSeatIsClickable,
                        onSpecialAction,
                        onPlayerSeatClick,
                        onCardSlotClick,
                        onCardSlotRightClick,
                    }: PlayerSeatProps) => {
    const isTouchSupported = useIsTouchDevice();

    const player = game.users.find(u => u.userId === playerId)!;
    const isCurrent = player.userId === game.gameData.currentPlayerId;
    const isOnline = player.gameUserData.connectionStatus === ConnectionStatus.ONLINE;

    const isGameGoing = game.state === GameState.MAIN_PLAY;
    const hasKnocked = game.gameData.lockedPlayerId === playerId;
    const isAnotherPLayerChoosingTheCard = game.gameData.turnState === TurnState.CARD_TAKE
        && !(game.gameData.currentPlayerId == playerId && isYou);

    const playerCards: PlayerCardHolder = player.gameUserData.cards;

    const rows: CardPosition[][] = splitCardsToRows(playerCards);
    const cardsCount: number = Object.keys(playerCards.cards).length;

    const onlineClass = isOnline ? styles["playerRow__statusDot--online"] : styles["playerRow__statusDot--offline"];

    function getEventInfoSection() {
        if (game.state === GameState.RESULTS_REVEAL) {
            return getScoreSection();
        }

        const requiredAction = player.gameUserData.actions.requiredAction;
        const tossAction = player.gameUserData.actions.tossAction;
        const optionalAction = player.gameUserData.actions.optionalAction;

        if (requiredAction === UserAction.SQUARE_CARD_SWAP && isCurrent && isYou) {
            return getSwapCardSection();
        }

        if (optionalAction === UserAction.KNOCK && isCurrent && isYou && !game.gameData.lockedPlayerId) {
            return getKnockSection();
        }

        if (game.gameData.lockedPlayerId === playerId) {
            return getKnockedStatusSection();
        }

        return (
            <div className={styles.gameScreen__handInfoCenter}>
                {requiredAction && (
                    <span key={`${playerId}${requiredAction}`} className={styles.gameScreen__handActionInfo}>
                        {USER_ACTION_LABELS[requiredAction]}
                    </span>
                )}
                {tossAction && (
                    <span key={`${playerId}${tossAction}`}
                          className={`${styles.gameScreen__handActionInfo} ${styles["gameScreen__handActionInfo-toss"]}`}>
                        {(isTouchSupported ? "Swipe: " : "Right Click: ") + USER_ACTION_LABELS[tossAction]}
                    </span>
                )}
                {!game.gameData.sameCardTossed && (game.gameData.turnState === TurnState.USER_ACTIONS || game.gameData.turnState === TurnState.FINISHING) && (
                    <span key={`${playerId}TOSS_CARD`}
                          className={`${styles.gameScreen__handActionInfo} ${styles["gameScreen__handActionInfo-toss"]}`}>
                        {(isTouchSupported ? "Swipe: " : "Right Click: ") + USER_ACTION_LABELS["TOSS_CARD"]}
                    </span>
                )}
                {optionalAction && !(optionalAction === UserAction.KNOCK && game.gameData.lockedPlayerId) && (
                    <span key={`${playerId}${optionalAction}`} className={styles.gameScreen__handActionInfo}>
                        {`${USER_ACTION_LABELS[optionalAction]}?`}
                    </span>
                )}
            </div>
        );
    }

    function getKnockedStatusSection() {
        return (
            <div className={styles.gameScreen__handInfoCenter}>
                    <span key={`${playerId}KNOCKED`} className={styles.gameScreen__handActionInfo}>
                            <strong>Knocked</strong>
                    </span>
            </div>
        );
    }

    function getScoreSection() {
        const className = `${styles.gameScreen__handActionInfo} ${game.gameData.winningPlayerIds.includes(playerId) ? styles['gameScreen__handActionInfo-won'] : ''}`;

        return (
            <div className={styles.gameScreen__handInfoCenter}>
                    <span key={`${playerId}POINTS`} className={className}>
                        {`Score: ${player.gameUserData.points}`}
                        {game.gameData.lockedPlayerId === playerId && <span className={styles.playerRow__knockedTag}>Knocked</span>}
                    </span>
            </div>
        );
    }

    function getSwapCardSection() {
        return (
            <div className={styles.gameScreen__handInfoButtons}>
                <span key={`${playerId}${UserAction.SQUARE_CARD_SWAP}`} className={styles.gameScreen__handActionInfo}>
                    {`${USER_ACTION_LABELS[UserAction.SQUARE_CARD_SWAP]}?`}
                </span>
                <button
                    className={`${styles["gameScreen__btn"]} ${styles["gameScreen__action-btn"]} ${styles["gameScreen__btn--accent"]}`}
                    onClick={() => onSpecialAction(playerId, true)}
                >
                    Yes
                </button>
                <button
                    className={`${styles["gameScreen__btn"]} ${styles["gameScreen__action-btn"]}`}
                    onClick={() => onSpecialAction(playerId, false)}
                >
                    No
                </button>
            </div>
        );
    }

    function getKnockSection() {
        return (
            <div className={styles.gameScreen__handInfoButtons}>
                <span key={`${playerId}KNOCK`} className={styles.gameScreen__handActionInfo}>
                    {`${USER_ACTION_LABELS["KNOCK"]}?`}
                </span>
                <button
                    className={`${styles["gameScreen__btn"]} ${styles["gameScreen__action-btn"]}`}
                    onClick={() => onSpecialAction(playerId, undefined, true)}
                >
                    Yes
                </button>
            </div>
        );
    }

    function constructPlayerSeatClassName() {
        const positionClass = styles[`gameScreen__seat--${position}`]
        const clickableClass = (playerSeatIsClickable && !hasKnocked && isGameGoing) ? styles["gameScreen__seat--clickable"] : '';

        const youAreCurrentPlayer = isCurrent && isGameGoing && isYou;
        const tossedByThisPlayer = game.gameData.lastTossCartEvent?.eventInitiatorId === playerId && isGameGoing;
        const thisPlayerKnocked = game.gameData.lockedPlayerId === playerId && isGameGoing;
        const lastEventWasToss = (game.gameData.lastTossCartEvent?.version ?? 0) >= (game.gameData.lastPlayerAction?.version ?? 0)

        let actionClass;

        if (thisPlayerKnocked) {
            actionClass = styles["gameScreen__seat--knocked"];
        } else if (tossedByThisPlayer && lastEventWasToss) {
            console.log("seat is toss")
            actionClass = styles["gameScreen__seat--tossMarker"];
        } else if (youAreCurrentPlayer) {
            actionClass = styles["gameScreen__seat--you--current"]
        }

        const isCurrentClass = isCurrent && isGameGoing ? styles["gameScreen__seat--current"] : "";

        return `${styles.gameScreen__seat} ${positionClass} ${isCurrentClass} ${clickableClass} ${actionClass}`;
    }

    return (
        <div
            key={`player-seat-${playerId}`}
            className={constructPlayerSeatClassName()}
            onClick={playerSeatIsClickable ? () => onPlayerSeatClick(playerId) : () => {
            }}
            onContextMenu={e => e.preventDefault()}
        >
            <div className={styles.gameScreen__handHeader}>
                <div className={styles.gameScreen__handInfoLeft}>
                    <div className={styles.playerRow__avatar}>
                        {toInitials(player.user.username)}
                    </div>
                    <div className={styles.playerRow__text}>
                        <div className={styles.playerRow__name}>
                            <span className={styles.playerRow__nameText}>
                                {player.user.username}
                            </span>
                            {isYou && (<span className={styles.playerRow__youTag}>You</span>)}
                        </div>
                        <div className={styles.playerRow__sub}>
                            Cards: {cardsCount}
                        </div>
                    </div>
                </div>
                {getEventInfoSection()}
                <div className={styles.gameScreen__handInfoRight}>
                    <span className={`${styles.playerRow__statusDot} ${onlineClass}`}/>
                </div>
            </div>

            {rows.length > 0 ? (
                <div className={styles.gameScreen__cardGrid}>
                    {rows.map((row, rowIndex) => (
                        <div
                            key={rowIndex}
                            className={styles.gameScreen__cardRow}
                        >
                            {row.map(slot =>
                                <CardSlot
                                    key={`card-slot-${playerId}-${slot.slot}`}
                                    playerId={playerId}
                                    cardSlot={slot.slot}
                                    card={slot.card}
                                    disabled={!isGameGoing || hasKnocked || isAnotherPLayerChoosingTheCard}
                                    size={"md"}
                                    game={game}
                                    onCardSlotClick={onCardSlotClick}
                                    onCardSlotRightClick={onCardSlotRightClick}
                                />
                            )}
                        </div>
                    ))}
                </div>
            ) : (
                <div className={styles.gameScreen__emptyHand}>No cards.</div>
            )}
        </div>
    );
};

export default PlayerSeat;